import * as React from 'react';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import UserContext from '../../../context/UserContext/UserContext';
import UserService from '../../../services/UserService/UserService';
import SuccessMessage from '../../../components/SuccessMessage/SuccessMessage';
import Button from '../../../components/Button/Button';
import { UpdateTab } from '../ProfilePage';
import CustomInput from '../../../components/CustomInput/CustomInput';

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
}

interface UpdateDetailsFormProps {
  handleChange: (event: React.SyntheticEvent, newValue: UpdateTab) => void;
}

function UpdateDetailsForm({
  handleChange
}: Readonly<UpdateDetailsFormProps>): React.ReactElement {
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [successMessage, setSuccessMessage] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const { user, setUser } = React.useContext(UserContext);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required')
  });

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema)
  });

  React.useEffect(() => {
    if (user) {
      setValue('firstName', user?.firstName);
      setValue('lastName', user?.lastName || '');
      setValue('email', user?.email);
    }
  }, [user, setValue]);

  const userIsUnchanged = (data: FormValues): boolean => {
    if (
      data.email === user?.email &&
      data.firstName === user.firstName &&
      data.lastName === user.lastName
    ) {
      return true;
    }
    return false;
  };

  const onSubmit = (data: FormValues): void => {
    if (userIsUnchanged(data)) {
      setErrorMessage('Details are unchanged');
      return;
    }
    const { email, firstName, lastName } = data;
    setIsLoading(true);
    UserService.updateUserDetails(email, firstName, lastName)
      .then((res) => {
        setUser({
          ...res.user,
          mfaComplete: true
        });
        setErrorMessage('');
        setSuccessMessage('User details updated');
        reset();
      })
      .catch((err) => {
        setSuccessMessage('');
        if (err.name === 'CanceledError') return;
        const error =
          err.apiError?.message ||
          'Something went wrong. Please try again later.';
        setErrorMessage(error);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <fieldset className='border-0 p-0 mb-4'>
        <div className='w-full'>
          <Controller
            name='firstName'
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label='First name'
                autoComplete='given-name'
                error={errors.firstName}
              />
            )}
          />
          <Controller
            name='lastName'
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label='Last name'
                autoComplete='family-name'
                error={errors.lastName}
              />
            )}
          />
        </div>
      </fieldset>
      <div className='border-b border-gray-200 my-4' />
      <fieldset className='border-0 p-0 mb-2'>
        {user?.hasPassword ? (
          <div className='w-full'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <CustomInput
                  {...field}
                  label='Email'
                  type='email'
                  autoComplete='email'
                  error={errors.email}
                />
              )}
            />
          </div>
        ) : (
          <p className='text-sm text-gray-600 my-2'>
            You have logged in via social auth. Please{' '}
            <button
              type='button'
              onClick={(e) => handleChange(e, 'password')}
              className='text-blue-600 cursor-pointer hover:underline'
            >
              set a password
            </button>{' '}
            before changing your email.
          </p>
        )}
      </fieldset>
      <ErrorMessage message={errorMessage} />
      <SuccessMessage message={successMessage} />
      <Button type='submit' loading={isLoading} className='mt-3'>
        Update details
      </Button>
    </form>
  );
}

export default UpdateDetailsForm;
